<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Progress Indicator -->
    <report-progress :showProgress="isLoading" />

    <!-- Table Container with Sticky Header -->
    <div class="flex-1 overflow-hidden mb-16">
      <div class="h-full flex flex-col">
        <!-- Single Table with Sticky Header -->
        <div class="flex-1 overflow-auto">
          <table class="w-full table-auto text-sm">
            <thead class="sticky top-0 z-10">
              <tr>
                <th
                  v-if="isDayPeriod"
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium rounded-s-lg py-2"
                  rowspan="2"
                ></th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium pe-2 py-2"
                  rowspan="2"
                >
                  {{ $t("reports.performance_period_report.head_period") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="2"
                >
                  {{ $t("reports.performance_period_report.head_offered") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pt-2"
                  colspan="2"
                >
                  {{ $t("reports.performance_period_report.head_answered") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pt-2"
                  colspan="2"
                >
                  {{ $t("reports.performance_period_report.head_abandoned") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="2"
                >
                  {{
                    $t("reports.performance_period_report.head_answered_rate")
                  }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="2"
                >
                  {{
                    $t("reports.performance_period_report.head_service_level")
                  }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="2"
                >
                  {{
                    $t(
                      "reports.performance_period_report.head_average_talk_time"
                    )
                  }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="2"
                >
                  {{
                    $t(
                      "reports.performance_period_report.head_average_hold_time"
                    )
                  }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                  rowspan="2"
                >
                  {{
                    $t(
                      "reports.performance_period_report.head_average_handle_time"
                    )
                  }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pt-2"
                  rowspan="1"
                >
                  {{ $t("reports.performance_period_report.head_hold_count") }}
                </th>
                <th
                  v-for="i in 6"
                  :key="i"
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pt-2"
                  rowspan="1"
                >
                  {{ $t("reports.performance_period_report.head_answered") }}
                </th>
                <th
                  v-for="i in 6"
                  :key="i"
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pt-2"
                  rowspan="1"
                >
                  {{ $t("reports.performance_period_report.head_abandoned") }}
                </th>
              </tr>
              <tr>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pb-2"
                >
                  #
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pb-2"
                >
                  %
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pb-2"
                >
                  #
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pb-2"
                >
                  %
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pb-2"
                >
                  #
                </th>
                <th
                  v-for="i in [5, 10, 20, 30, 40, 50]"
                  :key="i"
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pb-2"
                >
                  {{ i }}
                </th>
                <th
                  v-for="i in [5, 10, 20, 30, 40, 50]"
                  :key="i"
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 pb-2"
                >
                  {{ i }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="isDayPeriod">
                <!-- Month rows (expandable) -->
                <template
                  v-for="(monthData, monthIndex) in groupedByMonth"
                  :key="monthData.month"
                >
                  <tr
                    :class="monthIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                    class="cursor-pointer hover:bg-gray-100"
                    @click="toggleMonth(monthData.month)"
                  >
                    <td
                      class="text-center text-primary-900 ps-2 py-2 border-b border-gray-200"
                    >
                      <v-icon size="small" color="primary-900">{{
                        expandedMonths[monthData.month]
                          ? "mdi-minus-circle"
                          : "mdi-plus-circle-outline"
                      }}</v-icon>
                    </td>
                    <td
                      class="text-center text-teal pe-2 py-2 border-b border-gray-200"
                    >
                      <span class="font-weight-bold">{{
                        monthData.month
                      }}</span>
                    </td>
                    <td
                      class="text-center text-grey-darken-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ monthData.totalOffered }}
                    </td>
                    <td
                      :class="
                        'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                        performanceState(monthData.totalSuccess, true)
                      "
                    >
                      {{ monthData.totalSuccess }}
                    </td>
                    <td
                      :class="
                        'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                        performanceState(monthData.totalSuccess, true)
                      "
                    >
                      {{ monthData.ansRate >= 0 ? Math.round(monthData.ansRate) + "%" : "-" }}
                    </td>
                    <td
                      :class="
                        'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                        performanceState(monthData.totalLost, false)
                      "
                    >
                      {{ monthData.totalLost }}
                    </td>
                    <td
                      :class="
                        'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                        performanceState(monthData.totalLost, false)
                      "
                    >
                      {{ monthData.dropRate >= 0 ? Math.round(monthData.dropRate) + "%" : "-" }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ formatSeconds(monthData.avgSpeedAns) }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{
                        monthData.avgServiceLevel >= 0
                          ? Math.round(monthData.avgServiceLevel) + "%"
                          : "-"
                      }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ formatSeconds(monthData.avgAverageTalkTime) }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ formatSeconds(monthData.avgAverageHoldTime) }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ formatSeconds(monthData.avgAverageHandleTime) }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ monthData.totalHoldCount ?? "-" }}
                    </td>
                    <!-- Placeholder cells for the time-based columns (5, 10, 20, 30, 40, 50 seconds) -->
                    <td
                      v-for="i in [5, 10, 20, 30, 40, 50]"
                      :key="'ans_' + i"
                      class="text-center px-2 py-2 border-b border-gray-200"
                    >
                      {{ monthData["ans_" + i] ?? "-" }}
                    </td>
                    <td
                      v-for="i in [5, 10, 20, 30, 40, 50]"
                      :key="'abd_' + i"
                      class="text-center px-2 py-2 border-b border-gray-200"
                    >
                      {{ monthData["abn_" + i] ?? "-" }}
                    </td>
                  </tr>
                  <!-- Day rows (shown when month is expanded) -->
                  <tr
                    v-for="day in monthData.days"
                    v-show="expandedMonths[monthData.month]"
                    :key="day.period"
                    :class="
                      (monthIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white') +
                      ' pl-8'
                    "
                  >
                    <td
                      class="text-center text-primary-900 ps-2 py-2 border-b border-gray-200"
                    ></td>
                    <td
                      class="text-center text-teal px-2 py-2 border-b border-gray-200"
                    >
                      {{ day.period }}
                    </td>
                    <td
                      class="text-center text-grey-darken-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ day.offered }}
                    </td>
                    <td
                      :class="
                        'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                        performanceState(day.success, true)
                      "
                    >
                      {{ day.success }}
                    </td>
                    <td
                      :class="
                        'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                        performanceState(day.success, true)
                      "
                    >
                      {{ day.ansRate >= 0 ? Math.round(day.ansRate) + "%" : "-" }}
                    </td>
                    <td
                      :class="
                        'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                        performanceState(day.lost, false)
                      "
                    >
                      {{ day.lost }}
                    </td>
                    <td
                      :class="
                        'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                        performanceState(day.lost, false)
                      "
                    >
                      {{ day.dropRate >= 0 ? Math.round(day.dropRate) + "%" : "-" }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ formatSeconds(day.avg_speed_ans) }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{
                        day.ans_sla >= 0 ? Math.round(day.ans_sla) + "%" : "-"
                      }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ formatSeconds(day.avg_talk_time) }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ formatSeconds(day.avg_hold_time) }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ formatSeconds(day.avg_handle_time) }}
                    </td>
                    <td class="text-center px-2 py-2 border-b border-gray-200">
                      {{ day.hold ?? "-" }}
                    </td>
                    <!-- Placeholder cells for the time-based columns (5, 10, 20, 30, 40, 50 seconds) -->
                    <td
                      v-for="i in [5, 10, 20, 30, 40, 50]"
                      :key="'ans_' + i"
                      class="text-center px-2 py-2 border-b border-gray-200"
                    >
                      {{ day["ans_" + i] ?? "-" }}
                    </td>
                    <td
                      v-for="i in [5, 10, 20, 30, 40, 50]"
                      :key="'abd_' + i"
                      class="text-center px-2 py-2 border-b border-gray-200"
                    >
                      {{ day["abn_" + i] ?? "-" }}
                    </td>
                  </tr>
                </template>
              </template>
              <template v-else>
                <!-- Summary row (if exists) -->
                <tr
                  v-if="processedReportData.total"
                  key="summary"
                  class="bg-primary-50 font-weight-bold"
                >
                  <td
                    class="text-center text-primary-900 px-2 py-2 border-b border-gray-200"
                  >
                    {{ $t("reports.performance_period_report.summary") || "Total" }}
                  </td>
                  <td
                    class="text-center text-grey-darken-1 px-2 py-2 border-b border-gray-200"
                  >
                    {{ processedReportData.total.offered }}
                  </td>
                  <td
                    :class="
                      'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                      performanceState(processedReportData.total.success, true)
                    "
                  >
                    {{ processedReportData.total.success }}
                  </td>
                  <td
                    :class="
                      'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                      performanceState(processedReportData.total.success, true)
                    "
                  >
                    {{ processedReportData.total.ansRate !== "" && processedReportData.total.ansRate >= 0 ? Math.round(processedReportData.total.ansRate) + "%" : "-" }}
                  </td>
                  <td
                    :class="
                      'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                      performanceState(processedReportData.total.lost, false)
                    "
                  >
                    {{ processedReportData.total.lost }}
                  </td>
                  <td
                    :class="
                      'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                      performanceState(processedReportData.total.lost, false)
                    "
                  >
                    {{ processedReportData.total.dropRate !== "" && processedReportData.total.dropRate >= 0 ? Math.round(processedReportData.total.dropRate) + "%" : "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ formatSeconds(processedReportData.total.avg_speed_ans) }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.ans_sla >= 0 ? Math.round(processedReportData.total.ans_sla) + "%" : "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ formatSeconds(processedReportData.total.avg_talk_time) }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ formatSeconds(processedReportData.total.avg_hold_time) }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ formatSeconds(processedReportData.total.avg_handle_time) }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.hold ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.ans_5 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.ans_10 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.ans_20 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.ans_30 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.ans_40 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.ans_50 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.abn_5 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.abn_10 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.abn_20 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.abn_30 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.abn_40 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ processedReportData.total.abn_50 ?? "-" }}
                  </td>
                </tr>
                <!-- Regular rows for non-day periods -->
                <tr
                  v-for="(call, index) in processedReportData.periods"
                  :key="call.period"
                  :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                >
                  <td
                    class="text-center text-teal px-2 py-2 border-b border-gray-200"
                  >
                    {{ call.period }}
                  </td>
                  <td
                    class="text-center text-grey-darken-1 px-2 py-2 border-b border-gray-200"
                  >
                    {{ call.offered }}
                  </td>
                  <td
                    :class="
                      'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                      performanceState(call.success, true)
                    "
                  >
                    {{ call.success }}
                  </td>
                  <td
                    :class="
                      'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                      performanceState(call.success, true)
                    "
                  >
                    {{ call.ansRate >= 0 ? Math.round(call.ansRate) + "%" : "-" }}
                  </td>
                  <td
                    :class="
                      'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                      performanceState(call.lost, false)
                    "
                  >
                    {{ call.lost }}
                  </td>
                  <td
                    :class="
                      'text-center text-uppercase px-2 py-2 border-b border-gray-200 text-' +
                      performanceState(call.lost, false)
                    "
                  >
                    {{ call.dropRate >= 0 ? Math.round(call.dropRate) + "%" : "-" }}
                  </td>

                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ formatSeconds(call.avg_speed_ans) }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.ans_sla >= 0 ? Math.round(call.ans_sla) + "%" : "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ formatSeconds(call.avg_talk_time) }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ formatSeconds(call.avg_hold_time) }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ formatSeconds(call.avg_handle_time) }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.hold ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.ans_5 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.ans_10 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.ans_20 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.ans_30 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.ans_40 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.ans_50 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.abn_5 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.abn_10 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.abn_20 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.abn_30 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.abn_40 ?? "-" }}
                  </td>
                  <td class="text-center px-2 py-2 border-b border-gray-200">
                    {{ call.abn_50 ?? "-" }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReportProgress from "../../components/ReportProgress.vue";
import moment from "moment";

export default {
  name: "performance-by-period-report",
  components: {
    ReportProgress,
  },
  props: {
    selectedFilters: {
      type: Object,
      default: () => ({}),
    },
    skills: {
      type: Array,
      default: () => [],
    },
    reportData: {
      type: [Array, Object],
      default: () => ({ periods: [], total: null }),
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    showCount: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedMonths: {},
    };
  },
  computed: {
    currentLocal() {
      return this.$i18n.locale;
    },
    isDayPeriod() {
      // Period value 2 means "days"
      return this.selectedFilters?.period === 2;
    },
    processedReportData() {
      if (!this.reportData || typeof this.reportData !== 'object') {
        return { total: null, periods: [] };
      }
      
      return {
        total: this.reportData.total || null,
        periods: this.reportData.periods || []
      };
    },
    groupedByMonth() {
      if (!this.isDayPeriod || !this.reportData || typeof this.reportData !== 'object') {
        return [];
      }

      const periods = this.reportData.periods || [];
      if (periods.length === 0) {
        return [];
      }

      const monthOrderValue = (month) => {
        const names = [
          "january",
          "february",
          "march",
          "april",
          "may",
          "june",
          "july",
          "august",
          "september",
          "october",
          "november",
          "december",
        ];
        const m = String(month || "").toLowerCase();
        const idx = names.indexOf(m);
        if (idx !== -1) return idx;
        const parsed = Date.parse(`${month} 1, 2000`);
        if (!isNaN(parsed)) return new Date(parsed).getMonth();
        return Number.MAX_SAFE_INTEGER;
      };

      return periods
        .map((item) => ({
          month: this.extractMonth(item.period),
          days: Array.isArray(item.days) ? item.days : [],
          totalOffered: parseInt(item.offered) || 0,
          totalSuccess: parseInt(item.success) || 0,
          totalLost: parseInt(item.lost) || 0,
          avgSpeedAns: parseFloat(item.avg_speed_ans) || 0,
          ansRate: parseFloat(item.ansRate) || 0,
          dropRate: parseFloat(item.dropRate) || 0,
          avgServiceLevel: parseFloat(item.ans_sla) || 0,
          avgAverageTalkTime: parseFloat(item.avg_talk_time) || 0,
          avgAverageHoldTime: parseFloat(item.avg_hold_time) || 0,
          avgAverageHandleTime: parseFloat(item.avg_handle_time) || 0,
          totalHoldCount: parseInt(item.hold) || 0,
          ans_5: parseInt(item["ans_5"]) || 0,
          ans_10: parseInt(item["ans_10"]) || 0,
          ans_20: parseInt(item["ans_20"]) || 0,
          ans_30: parseInt(item["ans_30"]) || 0,
          ans_40: parseInt(item["ans_40"]) || 0,
          ans_50: parseInt(item["ans_50"]) || 0,
          abn_5: parseInt(item["abn_5"]) || 0,
          abn_10: parseInt(item["abn_10"]) || 0,
          abn_20: parseInt(item["abn_20"]) || 0,
          abn_30: parseInt(item["abn_30"]) || 0,
          abn_40: parseInt(item["abn_40"]) || 0,
          abn_50: parseInt(item["abn_50"]) || 0,
        }))
        .sort((a, b) => monthOrderValue(a.month) - monthOrderValue(b.month));
    },
  },
  methods: {
    performanceState(stats, isSuccess) {
      if (!stats > 0) {
        return "grey-darken-1";
      }
      if (isSuccess) {
        return "success";
      } else {
        return "error";
      }
    },

    extractMonth(period) {
      // Try to extract month from various date formats
      // Examples: "2024-01-15", "January 2024", "01/15/2024", etc.
      if (!period) return "Unknown";

      // Try parsing as date
      const date = new Date(period);
      if (!isNaN(date.getTime())) {
        // Format as "Month Year" (e.g., "January 2024")
        return date.toLocaleDateString("en-US", {
          month: "long",
        });
      }

      // If it's already a month name format, return as is
      if (period.match(/^[A-Za-z]+\s+\d{4}$/)) {
        return period;
      }

      // Try to extract YYYY-MM pattern
      const yearMonthMatch = period.match(/(\d{4})-(\d{2})/);
      if (yearMonthMatch) {
        const date = new Date(
          yearMonthMatch[1],
          parseInt(yearMonthMatch[2]) - 1
        );
        return date.toLocaleDateString("en-US", {
          month: "long",
        });
      }

      return period;
    },

    toggleMonth(month) {
      // Use Vue 3 reactivity
      this.expandedMonths = {
        ...this.expandedMonths,
        [month]: !this.expandedMonths[month],
      };
    },
    formatSeconds(seconds) {
      if (seconds == null || seconds == undefined) return "-";
      return moment
        .utc(moment.duration(seconds, "seconds").asMilliseconds())
        .format("HH:mm:ss");
    },
  },
};
</script>

<style scoped></style>
